import { useMemo, useState } from 'react';
import { QUESTIONS, QUIZ_OPTIONS, type QuizQuestion } from '../data/questions';

export interface QuizAnswer {
  questionId: string;
  selected: 'safe' | 'suspicious';
  correct: boolean;
}

interface CheckQuizProps {
  onComplete: (answers: QuizAnswer[]) => void;
}

const CHANNEL_ICON: Record<QuizQuestion['type'], string> = {
  phishing: 'ri-mail-forbid-line',
  smishing: 'ri-message-2-line',
  vishing: 'ri-phone-line',
};

export function CheckQuiz({ onComplete }: CheckQuizProps) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [selected, setSelected] = useState<'safe' | 'suspicious' | null>(null);
  const [revealed, setRevealed] = useState(false);

  const question = QUESTIONS[index];
  const progress = useMemo(() => ((index + (revealed ? 1 : 0)) / QUESTIONS.length) * 100, [index, revealed]);
  const isLast = index === QUESTIONS.length - 1;

  const handleSelect = (option: 'safe' | 'suspicious') => {
    if (revealed) return;
    setSelected(option);
    setRevealed(true);
    setAnswers((prev) => [
      ...prev,
      {
        questionId: question.id,
        selected: option,
        correct: option === question.correctAnswer,
      },
    ]);
  };

  const handleNext = () => {
    if (isLast) {
      onComplete([
        ...answers,
      ]);
      return;
    }
    setIndex((i) => i + 1);
    setSelected(null);
    setRevealed(false);
  };

  const correctSoFar = answers.filter((a) => a.correct).length;
  const wasCorrect = selected !== null && selected === question.correctAnswer;

  return (
    <section className="relative pt-32 pb-20">
      <div className="container mx-auto px-6 max-w-3xl">

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Question {index + 1} of {QUESTIONS.length}</span>
            <span>{correctSoFar} correct so far</span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question card */}
        <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
              <i className={`${CHANNEL_ICON[question.type]} text-2xl text-blue-400`}></i>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-blue-400">{question.type}</div>
              <div className="text-white font-medium">{question.channel}</div>
            </div>
          </div>

          {question.sender && (
            <div className="text-sm text-gray-500 mb-3">
              <span className="text-gray-400">From:</span> <span className="text-gray-300">{question.sender}</span>
            </div>
          )}

          <p className="text-lg text-white leading-relaxed mb-8 whitespace-pre-line">
            {question.scenario}
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            {QUIZ_OPTIONS.map((opt) => {
              const isChosen = selected === opt.id;
              const isAnswer = question.correctAnswer === opt.id;
              const showState = revealed && (isChosen || isAnswer);

              let stateClasses = 'border-gray-700 hover:border-blue-400 hover:bg-blue-500/5 text-white';
              if (showState) {
                if (isAnswer) {
                  stateClasses = 'border-green-500/60 bg-green-500/10 text-green-300';
                } else if (isChosen) {
                  stateClasses = 'border-red-500/60 bg-red-500/10 text-red-300';
                }
              } else if (revealed) {
                stateClasses = 'border-gray-800 text-gray-500';
              }

              return (
                <button
                  key={opt.id}
                  onClick={() => handleSelect(opt.id)}
                  disabled={revealed}
                  className={`px-6 py-4 rounded-xl border transition-all duration-200 text-left flex items-center justify-between ${stateClasses} ${
                    revealed ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <span className="font-medium">{opt.label}</span>
                  {showState && (
                    <i
                      className={`ri-${isAnswer ? 'check' : 'close'}-line text-xl`}
                    ></i>
                  )}
                </button>
              );
              
            })}
          </div>
        </div>

        {/* Explanation */}
        {revealed && (
          <div
            className={`bg-gray-900/50 backdrop-blur-sm border rounded-2xl p-6 mb-6 ${
              wasCorrect ? 'border-green-500/30' : 'border-red-500/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <i
                className={`ri-${wasCorrect ? 'check-double' : 'error-warning'}-line text-2xl ${
                  wasCorrect ? 'text-green-400' : 'text-red-400'
                }`}
              ></i>
              <div>
                <div className={`font-medium mb-2 ${wasCorrect ? 'text-green-300' : 'text-red-300'}`}>
                  {wasCorrect ? 'Correct.' : 'That one would have got you.'}
                </div>
                <p className="text-gray-300 leading-relaxed">{question.explanation}</p>
              </div>
            </div>
          </div>
        )}

        {/* Next button */}
        {revealed && (
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              className="group px-6 py-3 rounded-full font-medium transition-all duration-300 cursor-pointer bg-white text-black hover:bg-blue-400 hover:text-white"
            >
              {isLast ? 'See my result' : 'Next scenario'}
              <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block">→</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
